import { FC, useEffect, useState } from 'react';
import { Avatar, Button, Col, Flex, Row } from 'antd';
import { TbBuilding, TbChevronDown, TbCrosshair, TbEyeMinus, TbPlus } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import OverflowFadeText from '@/shared/components/layouts/OverflowFadeText';
import WorkspacePrimitiveEditorModal from '@/shared/components/modals/ResourceCreationModals/WorkspacePrimitiveEditorModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import SiderTooltip from '@/shared/components/tooltips/SiderTooltip';
import { useUserStore, useWatchlistStore } from '@/stores';
import { WatchlistItem, WorkspaceSubjectType } from '@/types';
import { corporateLogo } from '@/utilities';

import WorkspaceResourceAvatar from '../../../workspace-resources/WorkspaceResourceAvatar';
import AnimatedCollapsibleContent from '../../siders/AnimatedCollapsibleContent';
import AnimatedSiderCollection from '../../siders/AnimatedSiderCollection';
import SiderHoverActionSlot from '../../siders/SiderHoverActionSlot';
import EmptySection from '../../views/EmptySection';

interface WorkspaceWatchlistsProps {
    collapsed?: boolean;
    groupCollapsed?: Record<string, boolean | undefined>;
    onCollapsedChange?: (collapsed: boolean) => void;
    onGroupCollapsedChange?: (watchlistId: string, collapsed: boolean) => void;
}

const resourceBySubjectType: Partial<Record<WorkspaceSubjectType, string>> = {
    [WorkspaceSubjectType.DASHBOARD]: 'dashboard',
    [WorkspaceSubjectType.FOLDER]: 'folder',
    [WorkspaceSubjectType.INVESTMENT]: 'investment',
    [WorkspaceSubjectType.LIFECYCLE]: 'lifecycle',
    [WorkspaceSubjectType.LIST]: 'list',
    [WorkspaceSubjectType.MEMO]: 'memo',
    [WorkspaceSubjectType.PIPELINE]: 'pipeline',
    [WorkspaceSubjectType.REPORT]: 'report',
    [WorkspaceSubjectType.TASK]: 'task',
    [WorkspaceSubjectType.TEAM]: 'team',
};

const routeForItem = (item: WatchlistItem) => {
    const routeBySubjectType: Partial<Record<WorkspaceSubjectType, string>> = {
        [WorkspaceSubjectType.DASHBOARD]: `/dashboards/${item.subjectId}`,
        [WorkspaceSubjectType.FOLDER]: `/folders/${item.subjectId}`,
        [WorkspaceSubjectType.INVESTMENT]: `/investments/${item.subjectId}`,
        [WorkspaceSubjectType.LIFECYCLE]: `/lifecycles/${item.subjectId}`,
        [WorkspaceSubjectType.LIST]: `/lists/${item.subjectId}`,
        [WorkspaceSubjectType.MEMO]: `/memos/${item.subjectId}`,
        [WorkspaceSubjectType.PIPELINE]: `/pipelines/${item.subjectId}`,
        [WorkspaceSubjectType.IMPORTED_ENTITY]: `/search/entities/${item.subjectId}`,
        [WorkspaceSubjectType.SEARCH_COMPANY]: `/search/companies/${item.subjectId}`,
        [WorkspaceSubjectType.TASK]: `/relations/tasks/${item.subjectId}`,
        [WorkspaceSubjectType.TEAM]: `/settings/workspace/permissions/teams/${item.subjectId}`,
    };
    const dashboardId = typeof item.metadata?.dashboardId === 'string' ? item.metadata.dashboardId : undefined;

    if (item.subjectType === WorkspaceSubjectType.REPORT && dashboardId) {
        return `/dashboards/${dashboardId}/reports/${item.subjectId}`;
    }

    return routeBySubjectType[item.subjectType] ?? '/';
};

const keyForWatchlistItem = (item: WatchlistItem) => `${item.subjectType}:${item.subjectId}`;

interface WatchlistResourceItemProps {
    item: WatchlistItem;
    removable: boolean;
    watchlistId: string;
    watchlistTitle: string;
}

const WatchlistResourceItem: FC<WatchlistResourceItemProps> = ({ item, removable, watchlistId, watchlistTitle }) => {
    const { token } = useAntDesignTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const toggleSubject = useWatchlistStore((state) => state.toggleSubject);
    const [hovering, setHovering] = useState(false);
    const website = typeof item.metadata?.website === 'string' ? item.metadata.website : undefined;
    const isCompany =
        item.subjectType === WorkspaceSubjectType.IMPORTED_ENTITY ||
        item.subjectType === WorkspaceSubjectType.SEARCH_COMPANY;
    const route = routeForItem(item);
    const active = route !== '/' && (location.pathname === route || location.pathname.startsWith(`${route}/`));
    const label = item.subjectLabel || item.subjectId;

    const removeFromWatchlist = () => {
        void toggleSubject(
            watchlistId,
            {
                metadata: item.metadata,
                subjectId: item.subjectId,
                subjectLabel: item.subjectLabel,
                subjectType: item.subjectType,
            },
            true,
            item.id,
        ).catch(() => {
            toast.error('Unable to remove this item from the watchlist.');
        });
    };

    return (
        <Row
            align='middle'
            style={{
                backgroundColor: active ? token.colorBorder : undefined,
                borderRadius: token.borderRadius,
                marginBottom: '0.25rem',
                width: '100%',
            }}
            wrap={false}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
        >
            <Col flex='auto' style={{ minWidth: 0 }}>
                <Button
                    block
                    size='small'
                    type='text'
                    style={{
                        backgroundColor: 'transparent',
                        color: token.colorText,
                        justifyContent: 'flex-start',
                        minWidth: 0,
                        overflow: 'hidden',
                        textAlign: 'left',
                    }}
                    onClick={() => navigate(route)}
                >
                    <Flex align='center' gap='0.375rem' style={{ minWidth: 0, width: '100%' }}>
                        {isCompany ? (
                            <Avatar
                                icon={<TbBuilding size='70%' style={{ color: token.colorTextTertiary }} />}
                                shape='square'
                                size={20}
                                src={website ? corporateLogo(website) : undefined}
                                style={{
                                    backgroundColor: token.colorBorderBg,
                                    border: `1px solid ${token.colorBorder}`,
                                    flex: '0 0 auto',
                                }}
                            />
                        ) : (
                            <WorkspaceResourceAvatar
                                listType={
                                    typeof item.metadata?.listType === 'string'
                                        ? item.metadata.listType
                                        : typeof item.metadata?.type === 'string'
                                          ? item.metadata.type
                                          : undefined
                                }
                                resource={resourceBySubjectType[item.subjectType] ?? 'resource'}
                                size={20}
                                style={{ flex: '0 0 auto' }}
                                website={website}
                            />
                        )}
                        <OverflowFadeText style={{ flex: '1 1 0%', width: 0 }}>{label}</OverflowFadeText>
                    </Flex>
                </Button>
            </Col>
            <SiderHoverActionSlot visible={hovering && removable}>
                <SiderTooltip title={`Remove from ${watchlistTitle}`}>
                    <Button
                        aria-label={`Remove ${label} from ${watchlistTitle}`}
                        className='left-sider-hover-action'
                        size='small'
                        type='text'
                        onClick={removeFromWatchlist}
                    >
                        <TbEyeMinus style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }} />
                    </Button>
                </SiderTooltip>
            </SiderHoverActionSlot>
        </Row>
    );
};

const WorkspaceWatchlists: FC<WorkspaceWatchlistsProps> = ({
    collapsed = false,
    groupCollapsed,
    onCollapsedChange,
    onGroupCollapsedChange,
}) => {
    const { token } = useAntDesignTheme();
    const activeWorkspaceId = useUserStore((state) => state.selectedUser?.activeWorkspace?.id);
    const [createWatchlistOpen, setCreateWatchlistOpen] = useState(false);
    const [sectionHovered, setSectionHovered] = useState(false);
    const {
        fetchWatchlist,
        fetchWatchlists,
        itemsByWatchlistId,
        loadedWatchlistIds,
        loading,
        loadingWatchlistIds,
        watchlistErrorsById,
        watchlists,
    } = useWatchlistStore();
    const visibleWatchlists = watchlists.filter((watchlist) => !watchlist.archived);

    useEffect(() => {
        void fetchWatchlists(activeWorkspaceId);
    }, [activeWorkspaceId, fetchWatchlists]);

    useEffect(() => {
        if (collapsed) {
            return;
        }

        watchlists
            .filter((watchlist) => !watchlist.archived && groupCollapsed?.[watchlist.id] === false)
            .filter(
                (watchlist) =>
                    !loadedWatchlistIds.includes(watchlist.id) &&
                    !loadingWatchlistIds.includes(watchlist.id) &&
                    !watchlistErrorsById[watchlist.id],
            )
            .forEach((watchlist) => {
                void fetchWatchlist(watchlist.id);
            });
    }, [
        collapsed,
        fetchWatchlist,
        groupCollapsed,
        loadedWatchlistIds,
        loadingWatchlistIds,
        watchlistErrorsById,
        watchlists,
    ]);

    const toggleWatchlist = (watchlistId: string) => {
        const nextCollapsed = !(groupCollapsed?.[watchlistId] ?? true);

        onGroupCollapsedChange?.(watchlistId, nextCollapsed);
        if (!nextCollapsed && !loadedWatchlistIds.includes(watchlistId)) {
            void fetchWatchlist(watchlistId);
        }
    };

    return (
        <>
            <div
                style={{
                    borderTop: `1px solid ${token.colorBorder}`,
                    padding: '0.5rem',
                }}
            >
                <Row
                    align='middle'
                    gutter={[12, 0]}
                    wrap={false}
                    onMouseEnter={() => setSectionHovered(true)}
                    onMouseLeave={() => setSectionHovered(false)}
                >
                    <Col flex='none'>
                        <Button
                            aria-label={`${collapsed ? 'Expand' : 'Collapse'} Watchlists`}
                            size='small'
                            type='text'
                            onClick={() => onCollapsedChange?.(!collapsed)}
                        >
                            <TbChevronDown
                                style={{
                                    color: token.colorTextTertiary,
                                    transform: collapsed ? 'rotate(-90deg)' : undefined,
                                    transition: 'transform 250ms ease',
                                }}
                            />
                        </Button>
                    </Col>
                    <Col flex='none'>
                        <small style={{ color: token.colorTextTertiary }}>
                            <TbCrosshair
                                size='0.875rem'
                                style={{ marginRight: '0.25rem', verticalAlign: '-0.175rem' }}
                            />
                            Watchlists
                        </small>
                    </Col>
                    <Col flex='auto' />
                    <SiderHoverActionSlot visible={sectionHovered}>
                        <SiderTooltip title='Create Watchlist'>
                            <Button
                                aria-label='Create Watchlist'
                                block
                                className='left-sider-hover-action'
                                size='small'
                                type='text'
                                onClick={() => setCreateWatchlistOpen(true)}
                            >
                                <TbPlus
                                    size='0.825rem'
                                    style={{ color: token.colorTextTertiary, verticalAlign: '-0.1rem' }}
                                />
                            </Button>
                        </SiderTooltip>
                    </SiderHoverActionSlot>
                </Row>
                <AnimatedCollapsibleContent collapsed={collapsed}>
                    <Row gutter={[12, 0]} wrap={false}>
                        <Col flex='none'>
                            <div
                                style={{
                                    background: token.colorBorder,
                                    borderRadius: token.borderRadius,
                                    height: '100%',
                                    margin: '0.25rem 0 0.25rem 0.675rem',
                                    width: '1.5px',
                                }}
                            />
                        </Col>
                        <Col flex='auto' style={{ minWidth: 0 }}>
                            <AnimatedSiderCollection>
                                {visibleWatchlists.map((watchlist) => {
                                    const watchlistCollapsed = groupCollapsed?.[watchlist.id] ?? true;
                                    const items = (itemsByWatchlistId[watchlist.id] ?? []).filter(
                                        (item) => item.subjectType !== WorkspaceSubjectType.LIFECYCLE,
                                    );
                                    const loadedItems = loadedWatchlistIds.includes(watchlist.id);
                                    const loadingItems = loadingWatchlistIds.includes(watchlist.id);
                                    const loadError = watchlistErrorsById[watchlist.id];

                                    return (
                                        <div key={watchlist.id} style={{ marginTop: '0.5rem' }}>
                                            <Button
                                                block
                                                size='small'
                                                type='text'
                                                style={{
                                                    justifyContent: 'flex-start',
                                                    marginBottom: watchlistCollapsed ? 0 : '0.5rem',
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                    textAlign: 'left',
                                                }}
                                                onClick={() => toggleWatchlist(watchlist.id)}
                                            >
                                                <Flex
                                                    align='center'
                                                    gap='0.375rem'
                                                    style={{ minWidth: 0, width: '100%' }}
                                                >
                                                    <TbCrosshair
                                                        style={{
                                                            color: token.colorTextTertiary,
                                                            flex: '0 0 auto',
                                                        }}
                                                    />
                                                    <OverflowFadeText
                                                        style={{
                                                            color: token.colorTextTertiary,
                                                            flex: '1 1 0%',
                                                            width: 0,
                                                        }}
                                                    >
                                                        <small>{watchlist.title}</small>
                                                    </OverflowFadeText>
                                                    <small
                                                        style={{
                                                            color: token.colorTextTertiary,
                                                            flex: '0 0 auto',
                                                        }}
                                                    >
                                                        {watchlist.itemCount}
                                                    </small>
                                                    <TbChevronDown
                                                        style={{
                                                            color: token.colorTextTertiary,
                                                            flex: '0 0 auto',
                                                            transform: watchlistCollapsed
                                                                ? 'rotate(-90deg)'
                                                                : undefined,
                                                            transition: 'transform 250ms ease',
                                                        }}
                                                    />
                                                </Flex>
                                            </Button>
                                            <AnimatedSiderCollection collapsed={watchlistCollapsed}>
                                                {loadingItems || (!loadedItems && !loadError) ? (
                                                    <small key='loading' style={{ color: token.colorTextTertiary }}>
                                                        Loading…
                                                    </small>
                                                ) : loadError ? (
                                                    <Flex align='center' gap='0.25rem' key='error'>
                                                        <small style={{ color: token.colorTextTertiary }}>
                                                            {loadError}
                                                        </small>
                                                        <Button
                                                            size='small'
                                                            style={{ height: 'auto' }}
                                                            type='link'
                                                            onClick={() => void fetchWatchlist(watchlist.id)}
                                                        >
                                                            Retry
                                                        </Button>
                                                    </Flex>
                                                ) : items.length ? (
                                                    items.map((item) => (
                                                        <WatchlistResourceItem
                                                            item={item}
                                                            key={keyForWatchlistItem(item)}
                                                            removable={watchlist.canEdit && !watchlist.locked}
                                                            watchlistId={watchlist.id}
                                                            watchlistTitle={watchlist.title}
                                                        />
                                                    ))
                                                ) : (
                                                    <small key='empty' style={{ color: token.colorTextTertiary }}>
                                                        No resources yet
                                                    </small>
                                                )}
                                            </AnimatedSiderCollection>
                                        </div>
                                    );
                                })}
                                {!loading && visibleWatchlists.length === 0 && (
                                    <EmptySection
                                        key='watchlists-empty'
                                        description='Create a watchlist to track companies and workspace resources here.'
                                        height='10rem'
                                        icon={<TbCrosshair size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                                        label='No Watchlists'
                                    />
                                )}
                            </AnimatedSiderCollection>
                        </Col>
                    </Row>
                </AnimatedCollapsibleContent>
            </div>
            <WorkspacePrimitiveEditorModal
                errorMessage='Unable to create this watchlist.'
                kind='watchlist'
                open={createWatchlistOpen}
                submitLabel='Create Watchlist'
                successMessage='Watchlist created.'
                onClose={() => setCreateWatchlistOpen(false)}
            />
        </>
    );
};

export default WorkspaceWatchlists;
